import { noop } from 'lodash-es';
import Mixpanel from 'mixpanel-browser';
import type { ReactNode } from 'react';
import { createContext, useCallback, useMemo } from 'react';

import type { AnalyticsEvent, Organization } from '../types';

type UserInAnalytics = {
  userKeyId: string;
  name: string;
  isTrader: boolean;
  email: string;
  roles: string[];
  isAdmin: boolean;
};

export type AnalyticsProviderProps = {
  user: Partial<UserInAnalytics>;
  organization?: Partial<Organization>;
  children: ReactNode;
  options: {
    enabled: boolean;
    token: string;
    debug?: boolean;
  };
};

type AnalyticsContextProps = {
  track: (event: AnalyticsEvent) => void;
  timeEvent: (eventName: string) => void;
};

export const AnalyticsContext = createContext<AnalyticsContextProps>({
  track: noop,
  timeEvent: noop,
});

export const AnalyticsProvider = ({
  user,
  organization,
  options,
  children,
}: AnalyticsProviderProps) => {
  const { userKeyId, ...appUserDetails } = user;

  const isMixpanelTrackingEnabled = useMemo(
    () => options.enabled ?? false,
    [options.enabled],
  );
  const shouldSendEventsToMixpanel = useMemo(
    () => isMixpanelTrackingEnabled && userKeyId !== undefined,
    [isMixpanelTrackingEnabled, userKeyId],
  );
  const organizationId = useMemo(
    () => organization?.organizationKeyID,
    [organization?.organizationKeyID],
  );

  const organizationOperator = organization?.operator;

  if (shouldSendEventsToMixpanel) {
    //https://developer.mixpanel.com/docs/javascript-full-api-reference#mixpanelset_config
    Mixpanel.init(options.token, {
      disable_persistence: true,
      debug: options.debug ?? false,
    });
    Mixpanel.identify(userKeyId);
  }

  const trackFn = useCallback(
    (event: AnalyticsEvent) => {
      if (shouldSendEventsToMixpanel) {
        Mixpanel.track(event.name, {
          ...(organizationId && { organization: organizationId }),
          ...(userKeyId && { userID: userKeyId }),
          ...appUserDetails,
          ...(organizationOperator && { organizationOperator }),
          ...(event.data ?? {}),
        });
      }
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [organizationId, organizationOperator, shouldSendEventsToMixpanel],
  );

  const timeEventFn = useCallback(
    (eventName: string) => {
      if (shouldSendEventsToMixpanel) {
        Mixpanel.time_event(eventName);
      }
    },

    [shouldSendEventsToMixpanel],
  );

  const providerValue = {
    track: trackFn,
    timeEvent: timeEventFn,
  };

  return (
    <AnalyticsContext.Provider value={providerValue}>
      {children}
    </AnalyticsContext.Provider>
  );
};
